<template>
    <div class="package-checkout">
        <div class="content mt-6">
            <h2 class="has-text-white mb-1">Checkout</h2>
            <p class="checkout-sub">Pay your bundle cost today. Your courses open immediately, and clock hour fees are billed later, only as you complete.</p>
        </div>

        <div class="package-card checkout-card">
            <!-- Order summary -->
            <div class="order-box">
                <div class="box-heading">Order Summary</div>
                <div class="order-rows">
                    <div class="row">
                        <div><strong>{{ pkgTargetHours }} Clock Hour Bundle</strong> &middot; bundle cost</div>
                        <div class="value">{{ pkgUpfront | currency }}</div>
                    </div>
                    <div class="row muted">
                        <div>Clock hour fees, billed as you complete &mdash; not charged today</div>
                        <div>{{ pkgDeferred | currency }}</div>
                    </div>
                    <div class="row muted" v-if="taxIncluded > 0">
                        <div>Includes sales tax<template v-if="pkgTaxRate"> ({{ (pkgTaxRate * 100).toFixed(2) }}%<template v-if="pkgTaxJurisdiction">, {{ pkgTaxJurisdiction }}</template>)</template></div>
                        <div>{{ taxIncluded | currency }}</div>
                    </div>
                    <div class="row total">
                        <div>Charged today</div>
                        <div>{{ chargeAmount | currency }}</div>
                    </div>
                </div>
            </div>

            <!-- Terms (toggle-acknowledged, CartCheckout style). The term text
                 must match the server-side getPackageSignatureClause verbatim
                 — it is part of the signature hash. -->
            <h4 class="section-title mt-6"><i class="far fa-list-check mr-2"></i>Terms and Acknowledgments</h4>
            <b-message type="is-info">
                <div class="term-row" v-for="term in terms" :key="term.key">
                    <div class="term-switch">
                        <b-switch type="is-success" v-model="ready[term.key]"/>
                    </div>
                    <div class="term-text"><strong>{{ term.title }}:</strong> {{ term.text }}</div>
                </div>
            </b-message>

            <!-- Signature -->
            <h4 class="section-title mt-6"><i class="far fa-file-signature mr-2"></i>Signature</h4>
            <p class="mb-4">By signing below, I confirm that I have read and agree to all terms and conditions stated above. I authorize the charge to my payment method for the total amount shown.</p>

            <div class="signature-container">
                <div class="signature-pad">
                    <div class="sig-line"></div>
                    <div class="sig-x">
                        <b-icon icon="times"/>
                    </div>
                    <div class="sig-name" v-if="currentUser">{{ currentUser.first_name }} {{ currentUser.last_name }}</div>
                    <div class="sig-buttons">
                        <b-button size="is-small" @click="undoSignature" class="mr-2">
                            <b-icon icon="undo" class="mr-1"/>
                            Undo
                        </b-button>
                        <b-button size="is-small" @click="clearSignature">
                            <b-icon icon="eraser" class="mr-1"/>
                            Clear
                        </b-button>
                    </div>
                    <vue-signature class="sig" ref="signature" :sigOption="signatureOption" :w="'1000px'" :h="'180px'"/>
                </div>
            </div>

            <!-- Payment -->
            <h4 class="section-title mt-6"><i class="far fa-credit-card mr-2"></i>Payment</h4>

            <div v-if="!clientSecret && !intentError" class="pb-3">
                <i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Establishing secure connection...
            </div>

            <b-message type="is-danger" v-if="intentError">
                <i class="far fa-exclamation-triangle mr-2"></i>{{ intentError }}
            </b-message>

            <div v-show="clientSecret">
                <p class="charge-line"><i class="far fa-info-circle mr-3"></i>Your card will be charged <strong>{{ chargeAmount | currency }}</strong> today &mdash; the bundle cost of your <strong>{{ pkgTargetHours }} Clock Hour Bundle</strong>.</p>
                <div class="card-container">
                    <div ref="card"></div>
                    <b-message type="is-danger" v-show="stripeErrors" class="mt-5">
                        <i class="far fa-exclamation-triangle mr-3"></i>{{ stripeErrors }}
                    </b-message>
                </div>
            </div>

            <hr class="mt-6"/>

            <b-button size="is-large" type="is-primary" expanded class="mt-5" :disabled="!isValid || processing" @click="complete">
                <i class="fas fa-spin fa-spinner mr-3" v-if="processing"></i>
                <i class="fas fa-lock mr-3" v-else></i>Pay {{ chargeAmount | currency }} &amp; Enroll
            </b-button>

            <div v-if="!isValid" class="todo-box mt-4">
                <div class="todo-title"><i class="fas fa-exclamation-triangle mr-2"></i>To complete your purchase, please:</div>
                <ul>
                    <li v-if="!allTermsReady">Review and accept each term above</li>
                    <li v-if="!signature">Sign the agreement above</li>
                    <li v-if="!ccReady">Complete your payment information</li>
                </ul>
            </div>

            <div class="has-text-centered mt-4">
                <b-button type="is-text" @click="$emit('back')"><i class="fas fa-arrow-left mr-2"></i>Back to review</b-button>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapGetters, mapActions} from "vuex";
    import {client as http, stripePK, verifySession} from "@/http_client";
    import {splitByUpfront} from "@/constants/packages";
    import moment from "moment";
    import async from "async";
    import VueSignature from 'vue-signature';

    let stripe = Stripe(stripePK),
        elements = stripe.elements(),
        card = undefined;

    const TERM_MONTHS = 36;   // bundle courses are valid 3 years from enrollment

    export default {
        name: "PackageCheckout",
        components: {VueSignature},
        data: function() {
            let _this = this;
            return {
                clientSecret: null,
                intentId: null,
                intentCharge: 0,
                intentTax: null,
                intentError: null,
                stripeErrors: null,
                ccReady: false,
                processing: false,
                signature: null,
                ready: {
                    details: false,
                    cost: false,
                    fees: false,
                    expiration: false
                },
                signatureOption: {
                    penColor: 'rgb(0, 0, 0)',
                    backgroundColor: 'rgb(255, 255, 255)',
                    onEnd: function() {
                        _this.saveSignature();
                    }
                }
            };
        },
        computed: {
            ...mapGetters(['currentUser', 'pkgTargetHours', 'pkgSelections', 'pkgUpfront', 'pkgDeferred', 'pkgSplitUpfront', 'pkgTaxRate', 'pkgTaxJurisdiction']),
            deferredRate() {
                return splitByUpfront(this.pkgSplitUpfront).deferred;
            },
            // The server-quoted charge wins once the intent exists
            chargeAmount() {
                return this.intentCharge ? this.intentCharge / 100 : this.pkgUpfront;
            },
            taxIncluded() {
                if (this.intentTax !== null) return this.intentTax;
                return Math.round(this.pkgUpfront * this.pkgTaxRate * 100) / 100;
            },
            expirationDate() {
                return moment().add(TERM_MONTHS, 'months').format('MMMM D, YYYY');
            },
            // Sentences match getPackageSignatureClause on the server verbatim
            terms() {
                const money = this.$options.filters.currency;
                return [
                    {key: 'details', title: 'Bundle Details', text: `I have reviewed my bundle — ${this.pkgTargetHours} clock hours, the courses selected, and the hours allocated to each — and confirm my selections are correct.`},
                    {key: 'cost', title: 'Bundle Cost', text: `I understand the bundle cost of ${money(this.pkgUpfront)} is due today and is non-refundable.`},
                    {key: 'fees', title: 'Clock Hour Fees', text: `I understand clock hour fees of $${this.deferredRate.toFixed(2)} per clock hour are billed only as I complete my courses. Fees for hours I do not complete are never billed.`},
                    {key: 'expiration', title: 'Expiration', text: `I understand the courses in my bundle are valid for 3 years from the date of enrollment and will expire on ${this.expirationDate}. An additional fee may be assessed to reactivate an expired enrollment.`}
                ];
            },
            allTermsReady() {
                return this.terms.every(term => this.ready[term.key]);
            },
            isValid() {
                return !!(this.ccReady && this.allTermsReady && this.signature && this.clientSecret);
            }
        },
        methods: {
            ...mapActions(['getEnrollments']),
            purchasePayload() {
                return {
                    hours: this.pkgTargetHours,
                    split_upfront: this.pkgSplitUpfront,
                    selections: this.pkgSelections.map(s => ({course_id: s.id, name: s.name, hours: s.hours}))
                };
            },
            getPaymentIntent() {
                http.post('app/packages/purchase-intent', this.purchasePayload(), {force: true}).then(response => {
                    this.intentId = response.data.id;
                    this.clientSecret = response.data.clientSecret;
                    this.intentCharge = response.data.total * 100;
                    this.intentTax = response.data.taxAmount || 0;
                }).catch(err => {
                    console.log(err);
                    this.intentError = (err.response && err.response.data && err.response.data.error)
                        || 'We could not start a secure checkout. Please try again, or contact support@my247ed.com.';
                });
            },
            stripeInit() {
                if (card) {
                    card.unmount();
                }
                card = elements.create('card');
                this.$nextTick(() => {
                    card.mount(this.$refs.card);
                    card.on('change', event => {
                        this.stripeErrors = event.error ? event.error.message : '';
                        this.ccReady = !!(event.complete);
                    });
                });
            },
            saveSignature() {
                this.signature = this.$refs.signature.save();
            },
            clearSignature() {
                this.$refs.signature.clear();
                this.signature = null;
            },
            undoSignature() {
                this.$refs.signature.undo();
                this.saveSignature();
            },
            complete() {
                if (this.processing || !this.isValid) return;
                this.processing = true;

                async.series([
                    // Confirm the session is still alive BEFORE the card is
                    // charged — a stale tab can still confirm the payment with
                    // Stripe but then 401 on the purchase call below
                    (cb) => {
                        verifySession().then(() => cb()).catch(err => {
                            if (err.response && err.response.status === 401) {
                                window.alert('Your session has expired. Please sign in and try again. Your card has NOT been charged.');
                            }
                            cb(err || 'Session check failed');
                        });
                    },
                    (cb) => {
                        stripe.confirmCardPayment(this.clientSecret, {
                            payment_method: {
                                card: card,
                                billing_details: {
                                    name: this.currentUser.first_name + ' ' + this.currentUser.last_name,
                                    email: this.currentUser.email
                                }
                            }
                        }).then(result => {
                            if (result.error) {
                                this.stripeErrors = result.error.message;
                                cb(result.error.message);
                            } else if (result.paymentIntent.status === 'succeeded') {
                                cb();
                            }
                        });
                    },
                    (cb) => {
                        http.post('app/packages/purchase', {
                            ...this.purchasePayload(),
                            intent_id: this.intentId,
                            signature: this.signature
                        }).then(() => {
                            this.getEnrollments();
                            this.$emit('complete');
                            cb();
                        }).catch(err => {
                            console.log(err);
                            const serverMsg = err.response && err.response.data && err.response.data.error;
                            window.alert(serverMsg || 'Purchase failed. Please try again or contact support@my247ed.com');
                            cb(err);
                        });
                    }
                ], err => {
                    if (err) {
                        console.log(err);
                    }
                    this.processing = false;
                });
            }
        },
        mounted() {
            this.getPaymentIntent();
            this.stripeInit();
        }
    };
</script>

<style lang="scss" scoped>
    .package-checkout {
        // Full main-column width, left-aligned — same page anatomy as
        // CartCheckout/CourseEnroll (white header outside, card below)
        .checkout-sub {
            color: #c7d6e6;
        }

        .checkout-card {
            padding: 2.5rem 3rem 3rem;

            @media (max-width: 768px) {
                padding: 1.5rem;
            }
        }

        .order-box {
            border: 1px solid #e1e7ee;
            border-radius: 6px;
            padding: 1.2rem 1.4rem;

            .box-heading {
                font-size: 0.75rem;
                font-weight: bold;
                letter-spacing: 0.06em;
                text-transform: uppercase;
                color: #7a8a9d;
            }

            .order-rows {
                margin-top: 0.7rem;
                display: flex;
                flex-direction: column;
                gap: 0.4rem;

                .row {
                    display: flex;
                    justify-content: space-between;
                    gap: 16px;
                    font-size: 0.9rem;
                    color: #33475b;

                    .value {
                        font-weight: bold;
                    }

                    &.muted {
                        font-size: 0.85rem;
                        color: #7a8a9d;
                    }

                    &.total {
                        border-top: 1px solid #eef1f5;
                        margin-top: 0.4rem;
                        padding-top: 0.6rem;
                        font-size: 1rem;
                        font-weight: 900;

                        div:last-child {
                            color: #2e5f8f;
                        }
                    }
                }
            }
        }

        .section-title {
            font-size: 1rem;
            font-weight: 900;
            color: #33475b;
            margin-bottom: 1rem;

            i {
                color: #366ea5;
            }
        }

        .charge-line {
            font-size: 0.9rem;
            color: #33475b;
            margin-bottom: 1rem;
        }

        .card-container {
            border: 1px solid #e7e7e7;
            border-radius: 8px;
            padding: 2rem;
            margin-bottom: 1rem;
        }

        .term-row {
            display: flex;
            gap: 0.4rem;
            margin-bottom: 1rem;

            &:last-child {
                margin-bottom: 0;
            }

            .term-switch {
                flex-shrink: 0;
            }

            .term-text {
                font-size: 0.9rem;
            }
        }

        .todo-box {
            background-color: #fff3cd;
            border: 1px solid #ffc107;
            border-radius: 4px;
            padding: 1rem;
            color: #856404;

            .todo-title {
                font-weight: 600;
                margin-bottom: 0.5rem;
            }

            ul {
                margin: 0 0 0 2rem;

                li {
                    margin-bottom: 0.25rem;
                }
            }
        }

        // Signature container/pad (matches CourseEnroll.vue)
        .signature-container {
            overflow-x: auto;
            overflow-y: hidden;
            max-width: 100%;
        }

        .signature-pad {
            position: relative;
            background: white;
            max-width: 100%;
            margin-top: 1rem;
            border: 1px solid #e7e7e7;
            border-radius: 8px;

            .sig {
                border: none;
            }

            .sig-x {
                position: absolute;
                bottom: 45px;
                left: 20px;
                font-size: 1.5rem;
            }

            .sig-name {
                position: absolute;
                bottom: 20px;
                left: 50px;
                font-size: 1rem;
                user-select: none;
            }

            .sig-line {
                position: absolute;
                bottom: 45px;
                left: 20px;
                right: 20px;
                border-bottom: 2px solid red;
            }

            .sig-buttons {
                position: absolute;
                top: 10px;
                right: 20px;
            }
        }
    }
</style>
